import React, {useContext, useEffect, useState} from "react";
import {Box, Button, InputBase, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Add01Icon, Search01Icon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {SignInContext} from "../../context/SignInContext";
import useConfirm from "../../hooks/useConfirm";
import {controlSx, Panel} from "../registry-project/dashboard/components/Panel";
import {BackendConfigurationRepository} from "../../repositories/BackendConfigurationRepository";
import {BackendConfigurationsTable} from "./components/backend-configurations-table";
import {BackendConfigurationDialog} from "./components/backend-configuration-dialog";
import {SERVICE_FIELDS} from "./serviceFields";

const getErrorMessage = (e, fallback) => e?.response?.data?.message || fallback;

const headerButtonSx = {
  height: 38, px: 1.75, borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 700,
  color: "#374151", bgcolor: "#FAFAFD", "&:hover": {bgcolor: "#ECECF3"},
};

export const BackendConfigurations = () => {
  const {currentUser, organizations} = useContext(SignInContext);
  const canManage = currentUser?.roles?.some(role => role === "admin" || role === "manager");

  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedConfiguration, setSelectedConfiguration] = useState(null);
  const [search, setSearch] = useState("");

  const confirmDelete = useConfirm(
    "Are you sure you want to delete this backend configuration?",
    "Projects that use this configuration may stop working."
  );

  useEffect(() => {
    BackendConfigurationRepository.getAll()
      .then(response => setConfigurations(response.data || []))
      .catch(e => {
        console.error(e);
        toast.error(getErrorMessage(e, "Failed to load backend configurations"));
      })
      .finally(() => setLoading(false));
  }, []);

  const getOrgName = (orgId) => {
    if (!orgId) return "Public";
    return organizations?.find(o => o.id === orgId)?.name || orgId;
  };

  const query = search.trim().toLowerCase();
  const filteredConfigurations = query
    ? configurations.filter(configuration => [
      configuration.name,
      getOrgName(configuration.organizationId),
      ...SERVICE_FIELDS.map(field => configuration[field.name]),
    ].some(value => String(value || "").toLowerCase().includes(query)))
    : configurations;

  const handleOpenModal = (configuration = null) => {
    setSelectedConfiguration(configuration);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedConfiguration(null);
  };

  const handleSave = async (configuration) => {
    try {
      if (selectedConfiguration?.id) {
        const response = await BackendConfigurationRepository.edit(selectedConfiguration.id, configuration);
        setConfigurations(prev => prev.map(c => c.id === selectedConfiguration.id ? response.data : c));
      } else {
        const response = await BackendConfigurationRepository.create(configuration);
        setConfigurations(prev => [...prev, response.data]);
      }
      toast.success("Backend configuration saved successfully");
      return true;
    } catch (e) {
      console.error(e);
      toast.error(getErrorMessage(e, "Failed to save backend configuration"));
      return false;
    }
  };

  const handleDelete = async (configuration) => {
    const confirmed = await confirmDelete({title: `Delete "${configuration.name}"?`});
    if (!confirmed) return;
    try {
      await BackendConfigurationRepository.delete(configuration.id);
      setConfigurations(prev => prev.filter(c => c.id !== configuration.id));
      toast.success("Backend configuration deleted successfully");
    } catch (e) {
      console.error(e);
      toast.error(getErrorMessage(e, "Failed to delete backend configuration"));
    }
  };

  return (
    <>
      <BackendConfigurationDialog
        open={openModal}
        selectedConfiguration={selectedConfiguration}
        handleClose={handleCloseModal}
        organizations={organizations}
        onSubmit={handleSave}
      />
      <Stack gap={3} sx={{p: {xs: 2, md: 3}, maxWidth: 1200, width: "100%", mx: "auto", boxSizing: "border-box"}}>
        <Stack direction={{xs: "column", md: "row"}} justifyContent={"space-between"} alignItems={{md: "flex-end"}} gap={2}>
          <Box>
            <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7"}}>WORKSPACE</Typography>
            <Typography component={"h1"} sx={{fontSize: 26, fontWeight: 700, color: "#23264D", letterSpacing: "-0.02em"}}>
              Backend Configuration
            </Typography>
            <Typography sx={{fontSize: 14, color: "#6B6B80"}}>
              Manage the service URLs projects connect to
            </Typography>
          </Box>
          <Stack direction={"row"} alignItems={"center"} gap={1.25} flexWrap={"wrap"}>
            <Box component={"label"} sx={{...controlSx, width: 220}}>
              <HugeiconsIcon icon={Search01Icon} size={16}/>
              <InputBase
                placeholder={"Search configurations..."}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                inputProps={{"aria-label": "Search configurations"}}
                sx={{flex: 1, fontSize: 13, color: "#23264D"}}
              />
            </Box>
            {canManage && (
              <Button
                sx={{...headerButtonSx, textWrap: "nowrap", flexShrink: 0}}
                startIcon={<HugeiconsIcon icon={Add01Icon} size={17}/>}
                onClick={() => handleOpenModal()}
              >
                Add Configuration
              </Button>
            )}
          </Stack>
        </Stack>

        <Panel sx={{overflow: "hidden"}}>
          <BackendConfigurationsTable
            configurations={filteredConfigurations}
            loading={loading}
            emptyText={configurations.length === 0 ? "No backend configurations added yet" : `No configurations match "${search}"`}
            getOrgName={getOrgName}
            canManage={canManage}
            onEdit={handleOpenModal}
            onDelete={handleDelete}
          />
        </Panel>
      </Stack>
    </>
  );
};
